import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

// ── Steps data ─────────────────────────────────────────────────────
const SETUP_STEPS = [
  {
    id: 1,
    title: "Start Your Google Ads Signup",
    icon: "🚀",
    tag: "ads.google.com",
    content: [
      {
        type: "link",
        label: "👉 Click here to open Google Ads signup",
        url: "https://ads.google.com",
        note: "Opens in a new tab — keep PilotLeadGen open so you can follow along",
      },
      {
        type: "text",
        value: "On the Google Ads page, click the blue Sign Up button. If you have a Google Ads credit code, click 'Claim your credit' first before signing up.",
      },
      {
        type: "note",
        value: "You'll land on a welcome screen that says: \"Create your first campaign in a few simple steps.\" Don't start the campaign yet — we'll guide you through skipping it properly.",
      },
    ],
  },
  {
    id: 2,
    title: "Dismiss the Welcome Modal",
    icon: "👆",
    tag: "Step 2 of 6",
    content: [
      {
        type: "text",
        value: "Click anywhere on the screen to close the welcome popup.",
      },
      {
        type: "text",
        value: "You'll land on a page that says: \"Hi [your name], Add your information to get started.\"",
      },
      {
        type: "note",
        value: "This is your starting screen — it shows your Google account name and has two fields: your business name and your website. You are in the right place.",
      },
    ],
  },
  {
    id: 3,
    title: "Enter Your Business Info",
    icon: "🏢",
    tag: "Business Details",
    content: [
      {
        type: "text",
        value: "Fill in your Business name in the first field.",
      },
      {
        type: "text",
        value: "Under \"Where should people go after clicking your ad?\" — select Your website.",
      },
      {
        type: "text",
        value: "Paste your website URL or landing page URL (e.g. https://yourwebsite.com or https://getstarted.pilotleadgen.com).",
      },
      {
        type: "note",
        value: "If you don't have a website yet, you can use your WhatsApp link or a landing page. You can always update this later.",
      },
      {
        type: "text",
        value: "Click Next to continue.",
      },
    ],
  },
  {
    id: 4,
    title: "Skip the Campaign Creation Steps",
    icon: "⏭️",
    tag: "Important — Read Carefully",
    content: [
      {
        type: "warning",
        value: "Google will try to walk you through building a campaign right away — choosing goals, keywords, ad copy, budget. DO NOT build a campaign here. You'll build your real campaign inside PilotLeadGen where it's done properly.",
      },
      {
        type: "text",
        value: "Click Skip (or Back → Skip) on each screen you see.",
      },
      {
        type: "text",
        value: "You'll eventually see a popup that says: \"Leave campaign creation? You can skip ahead to your account and create a campaign later.\"",
      },
      {
        type: "action",
        value: "Click Leave campaign creation to proceed to your account.",
      },
    ],
  },
  {
    id: 5,
    title: "Set Up Billing (Do NOT Skip This)",
    icon: "💳",
    tag: "Required Step",
    content: [
      {
        type: "warning",
        value: "This is the one step you cannot skip. Google requires a valid billing profile before your account becomes active and before your ads can run.",
      },
      {
        type: "text",
        value: "When asked about personalised guidance — select No.",
      },
      {
        type: "note",
        value: "Why No? Personalised guidance means Google will assign you an account manager who will try to spend your budget for you. You don't need that — PilotLeadGen is your guide.",
      },
      {
        type: "text",
        value: "When asked about promotional offers — select No.",
      },
      {
        type: "note",
        value: "Why No? Promotional offers usually come with spending requirements that could pressure you to spend money before you're ready. Skip it for now — focus on getting your account set up properly first.",
      },
      {
        type: "text",
        value: "Click the blue + button to create a new payment profile.",
      },
      {
        type: "text",
        value: "On the payment profile screen, click the blue + again to add a payment method (enter your card details).",
      },
      {
        type: "text",
        value: "Once your card is added, check the box to agree to the Google Ads Terms of Service.",
      },
      {
        type: "action",
        value: "Click Submit to complete billing setup.",
      },
    ],
  },
  {
    id: 6,
    title: "Verify Your Advertiser Identity",
    icon: "✅",
    tag: "2–3 Minutes",
    content: [
      {
        type: "text",
        value: "Google will prompt you through 2 short verification steps to confirm you're a legitimate advertiser.",
      },
      {
        type: "text",
        value: "Follow the prompts — it takes about 2–3 minutes. You'll need to confirm your name and possibly your phone number.",
      },
      {
        type: "success",
        value: "🎉 Congratulations! You now have a working Google Ads account. You're ahead of most beginners already.",
      },
    ],
  },
  {
    id: 6,
    title: "You're Ready to Build Your Campaign",
    icon: "🏆",
    tag: "Final Step",
    content: [
      {
        type: "success",
        value: "🎉 Congratulations! You now have a working Google Ads account. You're verified, billing is set up, and you're ahead of most beginners.",
      },
      {
        type: "text",
        value: "Your Google Ads account is live and ready. The next step is upgrading your PilotLeadGen plan to build your first real campaign using our Smart Campaign Builder.",
      },
      {
        type: "note",
        value: "💡 Conversion tracking setup is included as a bonus step inside your paid plan — we'll walk you through it after your campaign is built.",
      },
    ],
  },
];

// ── Step content renderer ──────────────────────────────────────────
function StepContent({ item }) {
  const base = { fontSize:14, fontFamily:'sans-serif', lineHeight:1.75, marginBottom:12 };
  switch (item.type) {
    case 'link':
      return (
        <div style={{ marginBottom:14 }}>
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display:'inline-flex', alignItems:'center', gap:8, background:'linear-gradient(135deg,#6366f1,#818cf8)', color:'#fff', padding:'12px 24px', borderRadius:12, fontSize:14, fontWeight:700, fontFamily:'sans-serif', textDecoration:'none', boxShadow:'0 4px 14px rgba(99,102,241,0.35)' }}
          >
            {item.label}
          </a>
          {item.note && (
            <p style={{ fontSize:12, color:'#94a3b8', fontFamily:'sans-serif', marginTop:8, marginBottom:0 }}>
              💡 {item.note}
            </p>
          )}
        </div>
      );
    case 'text':
      return <p style={{ ...base, color:'#334155' }}>{item.value}</p>;
    case 'note':
      return (
        <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:10, padding:'12px 16px', marginBottom:14 }}>
          <p style={{ ...base, color:'#1d4ed8', margin:0 }}>💡 {item.value}</p>
        </div>
      );
    case 'warning':
      return (
        <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:10, padding:'12px 16px', marginBottom:14 }}>
          <p style={{ ...base, color:'#c2410c', margin:0, fontWeight:600 }}>⚠️ {item.value}</p>
        </div>
      );
    case 'action':
      return (
        <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:'12px 16px', marginBottom:14 }}>
          <p style={{ ...base, color:'#166534', margin:0, fontWeight:700 }}>👉 {item.value}</p>
        </div>
      );
    case 'success':
      return (
        <div style={{ background:'linear-gradient(135deg,#f0fdf4,#dcfce7)', border:'1px solid #86efac', borderRadius:12, padding:'16px 20px', marginBottom:14 }}>
          <p style={{ ...base, color:'#166534', margin:0, fontWeight:700, fontSize:15 }}>{item.value}</p>
        </div>
      );
    case 'screenshot':
      return (
        <div style={{ background:'#f8faff', border:'1.5px dashed #c7d2fe', borderRadius:10, padding:'12px 16px', marginBottom:14 }}>
          <p style={{ ...base, color:'#4f46e5', margin:0, fontSize:13 }}>📸 Screenshot tip: {item.value}</p>
        </div>
      );
    case 'list':
      return (
        <ul style={{ margin:'0 0 14px 20px', padding:0 }}>
          {item.items.map((li, i) => (
            <li key={i} style={{ ...base, color:'#334155', marginBottom:6 }}>{li}</li>
          ))}
        </ul>
      );
    default:
      return null;
  }
}

// ── Main component ─────────────────────────────────────────────────
export default function Screen0() {
  const navigate  = useNavigate();
  const { user, userName, isPaid } = useAuth();
  const [step, setStep] = useState(0); // 0 = overview, 1-8 = step detail
  const [completed, setCompleted] = useState({});

  // Gate: free users → /upgrade, paid users → /dashboard
  const goToDashboard = () => {
    if (isPaid) {
      navigate('/dashboard');
    } else {
      navigate('/upgrade');
    }
  };

  const currentStep = SETUP_STEPS[step - 1];
  const totalSteps  = SETUP_STEPS.length;
  const doneCount   = Object.keys(completed).length;
  const allDone     = doneCount === totalSteps;

  const markDone = (id) => setCompleted(prev => ({ ...prev, [id]: true }));

  // ── Overview screen ────────────────────────────────────────────
  if (step === 0) {
    return (
      <div style={s.page}>
        <div style={s.header}>
          <Logo size={34} showText={true} textSize={16} />
          <button style={s.signInBtn} onClick={() => navigate(user ? '/dashboard' : '/signin')}>
            {user ? `👋 ${userName}` : 'Sign In'}
          </button>
        </div>

        <div style={s.hero}>
          <div style={s.heroBadge}>Free Plan — Google Ads Setup Guide</div>
          <h1 style={s.heroTitle}>Set Up Your Google Ads Account</h1>
          <p style={s.heroSub}>We'll walk you through every screen step by step. It takes about 15–20 minutes and you only do this once.</p>

          {/* Progress bar */}
          {doneCount > 0 && (
            <div style={{ maxWidth:400, margin:'20px auto 0' }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#64748b', fontFamily:'sans-serif', marginBottom:6 }}>
                <span>{doneCount} of {totalSteps} steps completed</span>
                <span>{Math.round((doneCount/totalSteps)*100)}%</span>
              </div>
              <div style={{ height:8, background:'#e2e8f0', borderRadius:99, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${(doneCount/totalSteps)*100}%`, background:'linear-gradient(90deg,#6366f1,#818cf8)', borderRadius:99, transition:'width 0.4s' }} />
              </div>
            </div>
          )}
        </div>

        {/* Step cards */}
        <div style={s.stepsGrid}>
          {SETUP_STEPS.map((st) => {
            const isDone = !!completed[st.id];
            return (
              <div key={st.id} style={{ ...s.stepCard, border:`2px solid ${isDone ? '#86efac' : '#e2e8f0'}`, background: isDone ? '#f0fdf4' : '#fff' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ width:36, height:36, borderRadius:10, background: isDone ? 'linear-gradient(135deg,#059669,#10b981)' : 'linear-gradient(135deg,#6366f1,#818cf8)', color:'#fff', fontWeight:800, fontSize:15, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'sans-serif', flexShrink:0 }}>
                      {isDone ? '✓' : st.id}
                    </div>
                    <div>
                      <div style={{ fontSize:14, fontWeight:700, color:'#1e293b', fontFamily:'sans-serif' }}>{st.title}</div>
                      <div style={{ fontSize:11, color:'#94a3b8', fontFamily:'sans-serif', marginTop:2 }}>{st.tag}</div>
                    </div>
                  </div>
                  <span style={{ fontSize:22 }}>{st.icon}</span>
                </div>
                <button
                  style={{ width:'100%', padding:'10px', borderRadius:10, background: isDone ? '#059669' : 'linear-gradient(135deg,#6366f1,#818cf8)', color:'#fff', border:'none', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'sans-serif' }}
                  onClick={() => setStep(st.id)}
                >
                  {isDone ? '✓ Review Step' : `Start Step ${st.id} →`}
                </button>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        {allDone ? (
          <div style={{ textAlign:'center', padding:'32px 24px 48px' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>🎉</div>
            <h2 style={{ fontSize:22, fontWeight:800, color:'#0f172a', fontFamily:'sans-serif', marginBottom:12 }}>You're All Set!</h2>
            <p style={{ fontSize:15, color:'#64748b', fontFamily:'sans-serif', marginBottom:24 }}>Your Google Ads account is ready. Now let's build your first campaign.</p>
            <button style={s.primaryBtn} onClick={goToDashboard}>
              Go to Campaign Builder →
            </button>
          </div>
        ) : (
          <div style={{ textAlign:'center', padding:'24px 24px 48px' }}>
            <p style={{ fontSize:13, color:'#94a3b8', fontFamily:'sans-serif', marginBottom:16 }}>Already have a Google Ads account?</p>
            <button
              style={{ ...s.primaryBtn, background:'#fff', color:'#6366f1', border:'2px solid #6366f1', boxShadow:'none' }}
              onClick={goToDashboard}
            >
              Skip to Campaign Builder →
            </button>
          </div>
        )}

        {/* Upgrade nudge */}
        <div style={s.upgradeBar}>
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:'#1e293b', fontFamily:'sans-serif' }}>🚀 Ready to build your first campaign?</div>
            <div style={{ fontSize:12, color:'#64748b', fontFamily:'sans-serif', marginTop:2 }}>Upgrade to Starter or Pro to unlock the full Campaign Builder</div>
          </div>
          <button style={s.upgradeBtn} onClick={() => navigate('/plans')}>
            View Plans →
          </button>
        </div>
      </div>
    );
  }

  // ── Individual step screen ──────────────────────────────────────
  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.backBtn} onClick={() => setStep(0)}>← Back to Overview</button>
        <Logo size={32} showText={false} />
      </div>

      {/* Step progress */}
      <div style={{ padding:'16px 24px 0', maxWidth:700, margin:'0 auto' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
          {SETUP_STEPS.map(st => (
            <div
              key={st.id}
              onClick={() => setStep(st.id)}
              style={{ flex:1, height:4, borderRadius:99, background: completed[st.id] ? '#059669' : st.id === step ? '#6366f1' : '#e2e8f0', cursor:'pointer', transition:'background 0.2s' }}
            />
          ))}
        </div>
        <div style={{ fontSize:12, color:'#94a3b8', fontFamily:'sans-serif' }}>Step {step} of {totalSteps}</div>
      </div>

      {/* Step content */}
      <div style={{ maxWidth:700, margin:'0 auto', padding:'24px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:24 }}>
          <div style={{ width:48, height:48, borderRadius:14, background:'linear-gradient(135deg,#6366f1,#818cf8)', color:'#fff', fontWeight:800, fontSize:22, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            {currentStep.icon}
          </div>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'#6366f1', textTransform:'uppercase', letterSpacing:1, fontFamily:'sans-serif' }}>{currentStep.tag}</div>
            <h2 style={{ fontSize:22, fontWeight:800, color:'#0f172a', margin:0, fontFamily:'sans-serif' }}>{currentStep.title}</h2>
          </div>
        </div>

        <div style={{ background:'#fff', borderRadius:16, border:'1px solid #e2e8f0', padding:'24px', marginBottom:24, boxShadow:'0 2px 12px rgba(0,0,0,0.04)' }}>
          {currentStep.content.map((item, i) => (
            <StepContent key={i} item={item} />
          ))}
        </div>

        {/* Navigation buttons */}
        <div style={{ display:'flex', gap:12, marginBottom:16 }}>
          {step > 1 && (
            <button style={{ flex:1, padding:'14px', borderRadius:12, background:'#fff', border:'2px solid #e2e8f0', color:'#475569', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'sans-serif' }} onClick={() => setStep(s => s - 1)}>
              ← Previous
            </button>
          )}
          <button
            style={{ flex:2, padding:'14px', borderRadius:12, background:'linear-gradient(135deg,#059669,#10b981)', color:'#fff', border:'none', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'sans-serif', boxShadow:'0 4px 14px rgba(5,150,105,0.3)' }}
            onClick={() => {
              markDone(step);
              if (step < totalSteps) {
                setStep(s => s + 1);
              } else {
                setStep(0); // back to overview
              }
            }}
          >
            {step < totalSteps ? '✓ Done — Next Step →' : '✓ All Done — Back to Overview'}
          </button>
        </div>

        <button
          style={{ width:'100%', padding:'12px', borderRadius:12, background:'#fff', border:'1px solid #e2e8f0', color:'#94a3b8', fontSize:13, cursor:'pointer', fontFamily:'sans-serif' }}
          onClick={() => setStep(0)}
        >
          Back to Overview
        </button>
      </div>
    </div>
  );
}

const s = {
  page:        { minHeight:'100vh', background:'#f8faff', fontFamily:"'Georgia',serif" },
  header:      { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 24px', background:'#fff', borderBottom:'1px solid #e2e8f0', position:'sticky', top:0, zIndex:100 },
  signInBtn:   { padding:'8px 18px', borderRadius:10, background:'#6366f1', color:'#fff', border:'none', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'sans-serif' },
  backBtn:     { padding:'8px 16px', borderRadius:10, background:'#f1f5f9', color:'#475569', border:'none', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'sans-serif' },
  hero:        { textAlign:'center', padding:'40px 24px 32px', maxWidth:680, margin:'0 auto' },
  heroBadge:   { display:'inline-block', fontSize:12, fontWeight:700, color:'#6366f1', background:'#eef2ff', border:'1px solid #c7d2fe', padding:'6px 16px', borderRadius:20, fontFamily:'sans-serif', marginBottom:16 },
  heroTitle:   { fontSize:28, fontWeight:800, color:'#0f172a', margin:'0 0 12px', lineHeight:1.2 },
  heroSub:     { fontSize:15, color:'#475569', fontFamily:'sans-serif', lineHeight:1.65, margin:0 },
  stepsGrid:   { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:16, maxWidth:960, margin:'0 auto', padding:'0 24px 24px' },
  stepCard:    { borderRadius:16, padding:'20px', transition:'all 0.2s' },
  primaryBtn:  { padding:'16px 40px', borderRadius:12, background:'linear-gradient(135deg,#6366f1,#818cf8)', color:'#fff', border:'none', fontSize:16, fontWeight:700, cursor:'pointer', fontFamily:'sans-serif', boxShadow:'0 4px 20px rgba(99,102,241,0.3)' },
  upgradeBar:  { display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:16, background:'linear-gradient(135deg,#1e293b,#0f172a)', margin:'24px 24px 0', borderRadius:16, padding:'20px 24px' },
  upgradeBtn:  { padding:'12px 24px', borderRadius:12, background:'linear-gradient(135deg,#6366f1,#818cf8)', color:'#fff', border:'none', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'sans-serif', whiteSpace:'nowrap' },
};